/**
 * LabelProjectionService — Batch-optimized 3D→2D projection for label overlays
 *
 * Caches refMercator, scale, and canvas size once per frame so that
 * N projections cost O(N) arithmetic instead of N redundant fromLngLat() calls.
 *
 * Usage:
 *   service.beginFrame(map, mapCenter)
 *   const pos = service.project(position, yOffset)
 *   // or: service.projectBatch(items)
 */

import mapboxgl from 'mapbox-gl'
import { FEET_TO_METERS } from '@/constants/units'

export interface ScreenPosition {
  x: number
  y: number
  visible: boolean
}

interface FrameContext {
  refX: number
  refY: number
  refZ: number
  scale: number
  canvasWidth: number
  canvasHeight: number
  map: mapboxgl.Map
}

export interface BatchItem {
  id: string
  position: [number, number, number]
  yOffset: number
}

export class LabelProjectionService {
  private ctx: FrameContext | null = null

  /**
   * Call once per frame before any project() calls.
   * Caches the Mercator reference point, scale, and canvas dimensions.
   */
  beginFrame(map: mapboxgl.Map, mapCenter: { lat: number; lng: number }): void {
    const canvas = map.getCanvas?.()
    if (!canvas) {
      this.ctx = null
      return
    }

    const refMercator = mapboxgl.MercatorCoordinate.fromLngLat(
      [mapCenter.lng, mapCenter.lat],
      0
    )
    const scale = refMercator.meterInMercatorCoordinateUnits()

    this.ctx = {
      refX: refMercator.x,
      refY: refMercator.y,
      refZ: refMercator.z ?? 0,
      scale,
      canvasWidth: canvas.clientWidth,
      canvasHeight: canvas.clientHeight,
      map,
    }
  }

  /**
   * Project a single 3D position (feet from mapCenter) to screen coordinates.
   * Must call beginFrame() first.
   */
  project(position: [number, number, number], yOffset: number = -60): ScreenPosition {
    const ctx = this.ctx
    if (!ctx) return { x: 0, y: 0, visible: false }

    try {
      const offsetX = position[0] * FEET_TO_METERS
      const offsetZ = position[2] * FEET_TO_METERS

      const mercX = ctx.refX + offsetX * ctx.scale
      const mercY = ctx.refY + offsetZ * ctx.scale

      const newMercator = new mapboxgl.MercatorCoordinate(mercX, mercY, ctx.refZ)
      const lngLat = newMercator.toLngLat()
      const screenPoint = ctx.map.project(lngLat)

      const visible =
        screenPoint.x >= -50 &&
        screenPoint.x <= ctx.canvasWidth + 50 &&
        screenPoint.y >= -50 &&
        screenPoint.y <= ctx.canvasHeight + 50

      return {
        x: screenPoint.x,
        y: screenPoint.y + yOffset,
        visible,
      }
    } catch {
      return { x: 0, y: 0, visible: false }
    }
  }

  /**
   * Project a batch of items in one loop. Returns a Map<id, ScreenPosition>.
   */
  projectBatch(items: BatchItem[]): Map<string, ScreenPosition> {
    const result = new Map<string, ScreenPosition>()
    for (const item of items) {
      result.set(item.id, this.project(item.position, item.yOffset))
    }
    return result
  }
}

/** Singleton instance shared across all overlay components */
export const labelProjection = new LabelProjectionService()
